import React, { forwardRef, useEffect, useRef, lazy, Suspense, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, ArrowUpRight, Clock3, Crosshair, LogOut, Menu, Scissors, Target, UserRound, Monitor, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePlatform } from '../platform/PlatformContext';
import { api } from '../platform/api';
import { Account } from '../platform/PlatformScreens';
import { AccountEligibility } from '../platform/AccountEligibility';
import { legalContent } from './legalContent';
import { LegalBlocks } from './LegalBlocks';
import './site.css';

import DeadCenterPage from '../deadcenter/DeadCenterPage';
import NotFoundPage from './NotFoundPage';

export const SiteLink = forwardRef(({ href, onClick, children, ...props }, ref) => {
  const { navigate } = usePlatform();
  return <a ref={ref} href={href} {...props} onClick={event => {
    onClick?.(event);
    if (!event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!props.target || props.target === '_self') && href.startsWith('/')) {
      event.preventDefault(); navigate(href);
    }
  }}>{children}</a>;
});
SiteLink.displayName = 'SiteLink';

export const PlayLink = ({ onPlay, ...props }) => <SiteLink href="/perfectslice" {...props} onClick={event => { if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onPlay(); } }}/>;

export const CubitMark = () => <span className="cp-mark" aria-hidden="true"><i/><i/><i/></span>;

/* ── Mobile navigation drawer — portal-based ───────────────────
   Two completely independent fixed layers:
     Layer 400 = Backdrop   (semi-transparent, clicks to close)
     Layer 401 = Drawer panel (fully opaque, slides in from right)
   They are SIBLINGS in the DOM — no shared parent carries opacity.
──────────────────────────────────────────────────────────────── */
const MobileNavDrawer = ({ open, onClose }) => {
  const { user, guest, openAuth, setUser, refreshAuth, changed, navigate } = usePlatform();
  const hasProfile = user?.alias_confirmed || (!user && guest);
  const closeRef  = useRef(null);
  const scrollRef = useRef(0);
  const [signingOut, setSigningOut] = useState(false);

  /* Scroll lock — iOS-safe: fix body so momentum scroll stops */
  useEffect(() => {
    if (open) {
      scrollRef.current = window.scrollY;
      document.body.style.overflow  = 'hidden';
      document.body.style.position  = 'fixed';
      document.body.style.top       = `-${scrollRef.current}px`;
      document.body.style.width     = '100%';
      document.body.style.paddingRight = '0';
    } else {
      document.body.style.overflow  = '';
      document.body.style.position  = '';
      document.body.style.top       = '';
      document.body.style.width     = '';
      document.body.style.paddingRight = '';
      window.scrollTo(0, scrollRef.current);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top      = '';
      document.body.style.width    = '';
      document.body.style.paddingRight = '';
    };
  }, [open]);

  /* Escape key */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  /* Auto-focus close button when drawer opens */
  useEffect(() => {
    if (open && closeRef.current) closeRef.current.focus();
  }, [open]);

  const handleSignIn = useCallback(() => {
    onClose();
    requestAnimationFrame(openAuth);
  }, [onClose, openAuth]);

  const handleSignOut = useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await api('/auth/sign-out', { method: 'POST' });
      setUser(null);
      await refreshAuth();
      changed();
      onClose();
      navigate('/');
    } catch {
      /* Silent fail — auth state will be corrected on next page load. */
    } finally {
      setSigningOut(false);
    }
  }, [signingOut, setUser, refreshAuth, changed, onClose, navigate]);

  return createPortal(
    <>
      {/* ── LAYER 1: Backdrop ─────────────────────────────────
          ONLY background-color carries the dark tint.
          No opacity on the element itself — the panel above
          is a sibling and will NEVER inherit this darkness. */}
      <div
        className={`cp-drawer-backdrop${open ? ' cp-drawer-backdrop--open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
        data-testid="site-drawer-backdrop"
      />

      {/* ── LAYER 2: Drawer panel ─────────────────────────────
          Fully opaque cream background.
          z-index higher than backdrop — always on top.
          Slides in from the right (hamburger side). */}
      <nav
        id="site-nav-drawer"
        className={`cp-drawer-panel${open ? ' cp-drawer-panel--open' : ''}`}
        aria-label="Navigation menu"
        aria-hidden={!open}
        data-testid="site-burger-menu-content"
      >
        {/* Header row: wordmark + close button */}
        <div className="cp-drawer-head">
          <SiteLink
            href="/"
            className="cp-wordmark"
            onClick={onClose}
            aria-label="CubitPlay home"
            data-testid="drawer-home-link"
          >
            <CubitMark/><span>CubitPlay<span className="cp-logo-dot">.</span></span>
          </SiteLink>
          <button
            ref={closeRef}
            className="cp-drawer-close"
            onClick={onClose}
            aria-label="Close navigation menu"
            data-testid="site-drawer-close-button"
          >
            <X size={20} aria-hidden="true"/>
          </button>
        </div>

        {/* Nav body */}
        <div className="cp-drawer-body">
          {/* Games */}
          <div className="cp-drawer-group">
            <p className="cp-drawer-section-label" aria-hidden="true">GAMES</p>
            <SiteLink
              href="/perfectslice"
              className="cp-drawer-item"
              onClick={onClose}
              data-testid="drawer-perfect-slice"
            >
              <span className="cp-drawer-item-icon"><Scissors size={18} aria-hidden="true"/></span>
              <span className="cp-drawer-item-text">Perfect Slice</span>
              <ArrowRight size={15} className="cp-drawer-arrow" aria-hidden="true"/>
            </SiteLink>
            <SiteLink
              href="/deadcenter"
              className="cp-drawer-item"
              onClick={onClose}
              data-testid="drawer-dead-center"
            >
              <span className="cp-drawer-item-icon"><Target size={18} aria-hidden="true"/></span>
              <span className="cp-drawer-item-text">Dead Center</span>
              <span className="cp-drawer-new-badge" aria-label="New game">NEW</span>
            </SiteLink>
            {/* Coming soon placeholder */}
            <div
              className="cp-drawer-item cp-drawer-item--soon"
              aria-hidden="true"
              data-testid="drawer-coming-soon"
            >
              <span className="cp-drawer-item-icon"><span className="cp-drawer-soon-dot" aria-hidden="true">+</span></span>
              <span className="cp-drawer-item-text">More coming soon</span>
              <span className="cp-drawer-soon-badge">SOON</span>
            </div>
          </div>

          <div className="cp-drawer-divide" aria-hidden="true"/>

          {/* Account */}
          <div className="cp-drawer-group">
            {user?.alias_confirmed ? (
              /* Fully authenticated: username label + Profile + Sign Out */
              <>
                <div className="cp-drawer-user-row" data-testid="drawer-user-row">
                  <UserRound size={13} aria-hidden="true"/>
                  <span className="cp-drawer-user-name" data-testid="drawer-user-name">{user.alias}</span>
                </div>
                <SiteLink
                  href="/account"
                  className="cp-drawer-item"
                  onClick={onClose}
                  data-testid="drawer-account"
                >
                  <span className="cp-drawer-item-icon"><UserRound size={18} aria-hidden="true"/></span>
                  <span className="cp-drawer-item-text">Profile</span>
                  <ArrowRight size={15} className="cp-drawer-arrow" aria-hidden="true"/>
                </SiteLink>
                <button
                  className="cp-drawer-item cp-drawer-signout"
                  onClick={handleSignOut}
                  data-testid="drawer-signout"
                  disabled={signingOut}
                  aria-label="Sign out of CubitPlay"
                >
                  <span className="cp-drawer-item-icon"><LogOut size={18} aria-hidden="true"/></span>
                  <span className="cp-drawer-item-text">{signingOut ? 'Signing out…' : 'Sign out'}</span>
                </button>
              </>
            ) : hasProfile ? (
              /* Guest browser profile */
              <SiteLink
                href="/account"
                className="cp-drawer-item"
                onClick={onClose}
                data-testid="drawer-account"
              >
                <span className="cp-drawer-item-icon"><UserRound size={18} aria-hidden="true"/></span>
                <span className="cp-drawer-item-text">{guest?.alias}</span>
                <ArrowRight size={15} className="cp-drawer-arrow" aria-hidden="true"/>
              </SiteLink>
            ) : (
              /* Not signed in */
              <button
                className="cp-drawer-item"
                onClick={handleSignIn}
                data-testid="drawer-signin"
              >
                <span className="cp-drawer-item-icon"><UserRound size={18} aria-hidden="true"/></span>
                <span className="cp-drawer-item-text">Sign in</span>
                <ArrowRight size={15} className="cp-drawer-arrow" aria-hidden="true"/>
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="cp-drawer-footer">
          <p className="cp-drawer-tagline">Tiny games. Sharp instincts.</p>
        </div>
      </nav>
    </>,
    document.body
  );
};

/* ── Burger button (opens the drawer) ────────────────── */
const BurgerMenu = () => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        className="cp-burger"
        aria-label="Open navigation menu"
        aria-expanded={open}
        aria-controls="site-nav-drawer"
        onClick={() => setOpen(true)}
        data-testid="site-burger-menu-button"
      >
        <Menu size={22} aria-hidden="true"/>
      </button>
      <MobileNavDrawer open={open} onClose={close}/>
    </>
  );
};

export const SiteHeader = () => {
  const { user, guest, view, openAuth } = usePlatform();
  const hasProfile = user?.alias_confirmed || (!user && guest);
  let accountLabel = guest ? guest.alias : 'Sign in';
  if (user) accountLabel = user.alias_confirmed ? user.alias : 'Finish profile';
  return (
    <header className="cp-header" data-testid="site-header">
      <div className="cp-container cp-header-inner">
        <SiteLink href="/" className="cp-wordmark" data-testid="site-logo-link" aria-label="CubitPlay home"><CubitMark/><span>CubitPlay<span className="cp-logo-dot">.</span></span></SiteLink>
        <nav aria-label="Main navigation" className="cp-nav">
          <BurgerMenu/>
          <Button asChild variant="outline" className="cp-account-link"><SiteLink href={hasProfile ? '/account' : '/signin'} onClick={event => { if (!hasProfile && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) { event.preventDefault(); openAuth(); } }} data-testid="site-header-account-button" aria-current={view === '/account' || view === '/signin' ? 'page' : undefined} aria-label={user ? `Your CubitPlay account: ${user.alias}` : guest ? `Your browser profile: ${guest.alias}` : 'Sign in to CubitPlay'}><UserRound size={16}/><span>{accountLabel}</span></SiteLink></Button>
        </nav>
      </div>
    </header>
  );
};

export const SiteFooter = () => <footer className="cp-theme cp-footer cp-container" data-testid="site-footer">
  <div><SiteLink href="/" className="cp-wordmark cp-footer-wordmark" data-testid="site-footer-logo-link"><CubitMark/><span>CubitPlay.</span></SiteLink><p data-testid="footer-tagline">Tiny games. Sharp instincts.</p></div>
  <div className="cp-footer-right"><nav aria-label="Footer navigation"><SiteLink href="/perfectslice" data-testid="footer-perfect-slice-link">Perfect Slice</SiteLink><SiteLink href="/deadcenter" data-testid="footer-dead-center-link">Dead Center</SiteLink><SiteLink href="/privacy" data-testid="footer-privacy-link">Privacy</SiteLink><SiteLink href="/terms" data-testid="footer-terms-link">Terms</SiteLink></nav><small data-testid="footer-copyright">&copy; 2026 CubitPlay</small></div>
</footer>;

const SliceIllustration = ({ hero = false }) => <div className={`cp-slice-art ${hero ? 'cp-hero-art' : 'cp-feature-art'}`} role="img" aria-label={hero ? 'A mint geometric shape split by a precise diagonal cut' : 'Illustration of Perfect Slice: an orange shape with a diagonal cut and a 37 percent target'} data-testid={hero ? 'hero-slice-illustration' : 'feature-slice-illustration'}>
  <div className="cp-art-top"><span>{hero ? 'A LITTLE PRECISION. A LOT OF PLAY.' : 'ONE CUT. HOW CLOSE CAN YOU GET?'}</span><Crosshair size={17}/></div>
  <svg viewBox="0 0 440 340" aria-hidden="true" className="cp-art-svg">
    <g className="cp-art-guides" fill="none" stroke="currentColor" strokeWidth="1"><path d="M35 50h10m-5-5v10M395 50h10m-5-5v10M35 285h10m-5-5v10M395 285h10m-5-5v10"/><circle cx="220" cy="167" r="137" strokeDasharray="2 7"/></g>
    {hero ? <><g className="cp-floating-piece"><path d="M99 179L99 81Q99 55 127 55H292Q320 55 320 83V112Z" fill="var(--cp-mint)" stroke="var(--cp-green)" strokeWidth="1.3"/></g><path d="M106 207L327 140V246Q327 274 299 274H134Q106 274 106 246Z" fill="var(--cp-green)"/><circle cx="326" cy="65" r="24" fill="var(--cp-orange)"/><path d="M321 61l11 4-11 4Z" fill="var(--cp-paper)"/></> : <><g className="cp-floating-piece"><path d="M95 182C94 108 124 48 189 49C250 49 299 83 325 116L95 182Z" fill="var(--cp-slice-fill)" stroke="var(--cp-orange)" strokeWidth="1"/></g><path d="M101 211L331 145C345 226 279 279 210 285C134 292 105 265 101 211Z" fill="var(--cp-slice-fill)" stroke="var(--cp-orange)" strokeWidth="1"/></>}
    <g fill="none" stroke="var(--cp-ink)" strokeWidth="1.5"><path d="M53 212L385 112" strokeDasharray="5 7"/><circle cx="49" cy="213" r="5" fill="var(--cp-panel)"/><path d="M377 107l9 5-4 10"/></g>
  </svg>
  <div className="cp-target-note"><span>{hero ? 'TRUST YOUR' : 'CUT OFF'}</span><strong>{hero ? 'instinct.' : <>{37}<span>%</span></>}</strong></div>
  <div className="cp-art-bottom"><span>{hero ? 'SMALL GAMES. BIG \"ONE MORE\".' : 'ILLUSTRATIVE CUT · YOUR TARGET WILL VARY'}</span><span className="cp-art-dots"><i/><i/><i/></span></div>
</div>;

/* ── Dead Center homepage ad illustration ──────────── */
const DcHomeAdIllustration = () => (
  <div
    className="cp-dc-ad-art"
    role="img"
    aria-label="Dead Center game illustration: an irregular polygon shape with a player tap marker and a pink crosshair revealing the true mathematical center"
    data-testid="dc-homepage-ad-illustration"
  >
    <div className="cp-dc-ad-art-top">
      <span>TAP THE CENTER. ONE SHOT.</span>
      <Target size={15} aria-hidden="true"/>
    </div>
    <svg viewBox="0 0 440 320" className="cp-dc-ad-svg" aria-hidden="true">
      <defs>
        <linearGradient id="cp-dc-poly-g" x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#99F6E4"/>
          <stop offset="1" stopColor="#5EEAD4"/>
        </linearGradient>
      </defs>
      {/* Guide circles */}
      <circle cx="220" cy="158" r="142" fill="none" stroke="#CCFBF1" strokeDasharray="2 6" strokeWidth="1.1"/>
      <circle cx="220" cy="158" r="84"  fill="none" stroke="#CCFBF1" strokeDasharray="2 6" strokeWidth="0.8"/>
      {/* Ground shadow */}
      <ellipse cx="220" cy="300" rx="108" ry="8" fill="#D1FAE5" opacity=".4"/>
      {/* Main polygon */}
      <polygon
        points="78,175 138,62 300,55 374,148 328,262 116,268"
        fill="url(#cp-dc-poly-g)"
        stroke="#0F766E"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      {/* Specular highlight */}
      <polygon
        points="78,175 138,62 300,55 374,148 328,262 116,268"
        fill="white"
        fillOpacity=".06"
      />
      {/* Player tap marker */}
      <circle cx="254" cy="136" r="11" fill="rgba(20,184,166,0.14)"/>
      <circle cx="254" cy="136" r="7" fill="#fff" stroke="#14B8A6" strokeWidth="2"/>
      <circle cx="254" cy="136" r="2.5" fill="#0F766E"/>
      {/* Distance dashed line */}
      <line x1="254" y1="136" x2="219" y2="160" stroke="#F72585" strokeWidth="1.2" strokeDasharray="4 4" opacity=".65"/>
      {/* True centroid */}
      <circle cx="219" cy="160" r="13" fill="none" stroke="rgba(247,37,133,0.2)" strokeWidth="5.5"/>
      <path d="M205 160H233M219 147V173" stroke="#F72585" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="219" cy="160" r="4.5" fill="#F72585" stroke="#fff" strokeWidth="2"/>
      {/* Corner marks */}
      <path d="M32 38h12m-6-6v12M400 38h12m-6-6v12M32 278h12m-6-6v12" stroke="#CCFBF1" strokeWidth="1.5"/>
    </svg>
    <div className="cp-dc-score-chip" data-testid="dc-homepage-score-chip">
      <span>SCORE</span>
      <strong>92</strong>
    </div>
    <div className="cp-dc-ad-bottom">
      <span>DAILY CHALLENGE · PRECISION</span>
      <span className="cp-art-dots"><i/><i/><i/></span>
    </div>
  </div>
);

export const HomePage = ({ onPlayEndless }) => <main id="main-content" data-testid="cubitplay-homepage">
  {/* ── Hero ── */}
  <section className="cp-hero cp-container" aria-labelledby="cp-home-title">
    <div className="cp-hero-copy">
      <h1 id="cp-home-title" data-testid="home-heading">Tiny games.<br/>Sharp <span>instincts.</span></h1>
      <p className="cp-hero-description" data-testid="home-description">Quick browser games that test your eye, judgement and just how close you can get.</p>
      <Button asChild className="cp-primary"><PlayLink onPlay={onPlayEndless} data-testid="home-hero-play-perfect-slice-button">PLAY PERFECT SLICE<ArrowUpRight size={21}/></PlayLink></Button>
      <p className="cp-play-note" data-testid="home-play-note">No download. No signup required to play.</p>
    </div>
    <SliceIllustration hero/>
  </section>

  {/* ── Perfect Slice feature ── */}
  <section className="cp-feature-section cp-container" aria-labelledby="cp-feature-title">
    <div className="cp-section-label">
      <span className="cp-eyebrow" data-testid="first-game-label">FIRST UP</span>
      <span className="cp-section-rule"/>
      <span className="cp-index">01 / PERFECT SLICE</span>
    </div>
    <div className="cp-feature-card">
      <div className="cp-feature-copy">
        <Badge variant="outline" className="cp-game-badge" data-testid="perfect-slice-label"><Scissors size={13}/> PERFECT SLICE</Badge>
        <h2 id="cp-feature-title" data-testid="perfect-slice-feature-heading">Think you can<br/>eyeball it?</h2>
        <p data-testid="perfect-slice-feature-description">One straight cut. Get as close to the target percentage as you can.<br/><strong>Three tries. Your best counts.</strong></p>
        <div className="cp-metadata" data-testid="perfect-slice-metadata">
          <span><Clock3 size={14}/>30 sec</span>
          <span><Crosshair size={14}/>Precision</span>
          <span><Monitor size={14}/>Play in browser</span>
        </div>
        <Button asChild className="cp-primary cp-feature-button"><PlayLink onPlay={onPlayEndless} data-testid="perfect-slice-feature-play-now-button">PLAY NOW<ArrowRight size={19}/></PlayLink></Button>
      </div>
      <SliceIllustration/>
    </div>
  </section>

  {/* ── Dead Center: New Game Ad ── */}
  <section className="cp-dc-section cp-container" aria-labelledby="cp-dc-title">
    <div className="cp-section-label">
      <span className="cp-eyebrow cp-dc-eyebrow" data-testid="dead-center-ad-eyebrow">
        <span className="cp-live-dot cp-dc-pulse" aria-hidden="true"/>
        NEW GAME
      </span>
      <span className="cp-section-rule"/>
      <span className="cp-index">02 / DEAD CENTER</span>
    </div>
    <div className="cp-dc-card" data-testid="dead-center-homepage-ad">
      <div className="cp-dc-copy">
        <Badge variant="outline" className="cp-dc-badge" data-testid="dead-center-homepage-badge">
          <Target size={13} aria-hidden="true"/> DEAD CENTER
        </Badge>
        <h2 id="cp-dc-title" data-testid="dead-center-homepage-heading">
          Find the<br/>dead center.
        </h2>
        <p data-testid="dead-center-homepage-description">
          Five shapes. One tap each. Find the exact mathematical center.<br/>
          <strong>How precise is your instinct?</strong>
        </p>
        <div className="cp-metadata" data-testid="dead-center-homepage-metadata">
          <span><Clock3 size={14}/>~2 min</span>
          <span><Target size={14}/>Precision</span>
          <span><Monitor size={14}/>Play in browser</span>
        </div>
        <Button asChild className="cp-primary cp-dc-play-btn">
          <SiteLink href="/deadcenter" data-testid="dead-center-homepage-play-button">
            PLAY DEAD CENTER<ArrowRight size={19}/>
          </SiteLink>
        </Button>
        <p className="cp-play-note" data-testid="dead-center-homepage-play-note">No download. No signup required.</p>
      </div>
      <DcHomeAdIllustration/>
    </div>
  </section>
</main>;

export const LegalPage = ({ kind }) => {
  const content = legalContent[kind];
  return <main id="main-content" className="cp-legal cp-container" data-testid={`${kind}-page`}>
    <SiteLink href="/" className="cp-back" data-testid={`${kind}-back-link`}><ArrowLeft size={16}/>Back to CubitPlay</SiteLink>
    <article data-testid="legal-page-container" aria-labelledby={`${kind}-document-title`}>
      <h1 id={`${kind}-document-title`} data-testid={`${kind}-title`}>{content.title}</h1>
      <div className="cp-legal-preface" data-testid={`${kind}-preamble`}><LegalBlocks blocks={content.preamble} prefix={`${kind}-preamble`} preamble/></div>
      <nav className="cp-legal-contents" aria-label="On this page" data-testid={`${kind}-contents`}>
        {content.sections.map(section => <a key={section.number} href={`#${kind}-section-${section.number}`} data-testid={`${kind}-contents-${section.number}`}>{section.number}. {section.title}</a>)}
      </nav>
      <div className="cp-legal-sections" data-testid={`${kind}-sections`}>
        {content.sections.map(section => <section id={`${kind}-section-${section.number}`} key={section.number} data-testid={`${kind}-section-${section.number}`} aria-labelledby={`${kind}-heading-${section.number}`}>
          <h2 id={`${kind}-heading-${section.number}`} data-testid={`${kind}-heading-${section.number}`}><span>{section.number}.</span> {section.title}</h2>
          <div data-testid={`${kind}-content-${section.number}`}><LegalBlocks blocks={section.blocks} prefix={`${kind}-section-${section.number}`}/></div>
        </section>)}
      </div>
    </article>
    <SiteLink href={kind === 'privacy' ? '/terms' : '/privacy'} className="cp-back" data-testid={`${kind}-other-policy-link`}>Read the {kind === 'privacy' ? 'Terms of Use' : 'Privacy Policy'}<ArrowRight size={16}/></SiteLink>
  </main>;
};

const SignInPage = () => {
  const { openAuth, user, authLoading } = usePlatform();
  const started = useRef(false);
  useEffect(() => { if (!authLoading && !user?.alias_confirmed && !started.current) { started.current = true; openAuth(); } }, [authLoading, user, openAuth]);
  return <main id="main-content" className="cp-signin cp-container" data-testid="signin-page"><div className="cp-signin-symbol"><CubitMark/></div><p className="cp-eyebrow">ONE CUBITPLAY ACCOUNT</p><h1 data-testid="signin-heading">One account. All your CubitPlay games.</h1><p className="cp-signin-description" data-testid="signin-description">Save your scores. Join leaderboards. Keep your Daily Slice history.</p>{user?.alias_confirmed ? <Button asChild className="cp-primary"><SiteLink href="/account" data-testid="signin-account-link">{user.alias}<ArrowRight size={18}/></SiteLink></Button> : <Button className="cp-primary" data-testid="signin-continue-button" onClick={openAuth}>CONTINUE WITH EMAIL<ArrowRight size={18}/></Button>}{!user?.alias_confirmed && <div className="cp-auth-scope cp-signin-notice"><AccountEligibility prefix="signin-page"/></div>}<p className="cp-play-note" data-testid="signin-play-note">No password to remember.<br/>No account needed to play.</p><SiteLink href="/perfectslice" className="cp-back" data-testid="signin-guest-play-link">Just here to play?<ArrowRight size={16}/></SiteLink></main>;
};

const PublicContent = ({ view, onPlayEndless }) => {
  if (view === '/') return <HomePage onPlayEndless={onPlayEndless}/>;
  if (view === '/privacy' || view === '/terms') return <LegalPage kind={view.slice(1)}/>;
  if (view === '/signin') return <SignInPage/>;
  if (view === '/account') return <div id="main-content"><Account onPlayEndless={onPlayEndless}/></div>;
  if (view === '/deadcenter') return <DeadCenterPage/>;
  return <NotFoundPage/>;
};

export default function PublicSite({ onPlayEndless }) {
  const { view } = usePlatform();
  return <div className="cp-site"><a href="#main-content" className="cp-skip-link" data-testid="site-skip-link">Skip to content</a><SiteHeader/><PublicContent view={view} onPlayEndless={onPlayEndless}/><SiteFooter/></div>;
}
